/* DESIGN.md §6.2 (ADR 0028): the most prominent element on a patient screen. A light
   primary-tint band with a thick primary edge, the name large in ink, 88px, sticky at the
   top. Allergies stay white tags, in danger red only for a documented allergy. */
.banner {
  position: sticky;
  top: 0;
  z-index: var(--z-banner);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--space-1);
  min-height: 88px;
  padding: var(--space-3) var(--space-5);
  border: 1px solid var(--brand-tint-strong);
  border-left: 6px solid var(--brand);
  border-radius: var(--radius-card);
  background: var(--brand-tint);
  color: var(--ink);
  box-shadow: var(--shadow-card);
  font: var(--t-body);
}

.line {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1) var(--space-5);
  min-width: 0;
}

.name {
  font: var(--t-title);
  font-weight: 600;
  overflow-wrap: anywhere;
}

.nameLink {
  color: var(--ink);
  text-decoration: none;
  cursor: pointer;
}

.nameLink[data-hovered] {
  text-decoration: underline;
  text-underline-offset: 3px;
}

.nameLink[data-focus-visible] {
  outline: var(--focus-ring);
  outline-offset: var(--focus-offset);
}

.strong {
  font: var(--t-body-strong);
}

.id {
  font-family: var(--font-id);
}

.meta {
  font: var(--t-body);
}

.allergies {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
}

.allergyList {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}

/* A documented allergy: white tag with danger text and border (DESIGN.md §6.2). */
.allergyTag {
  padding: 0 var(--space-2);
  border: 1px solid var(--danger);
  border-radius: var(--radius-tag);
  background: var(--paper);
  color: var(--danger);
  font: var(--t-body-strong);
}

/* "Allergies not recorded": outlined, prompting capture. */
.outlinedTag {
  padding: 0 var(--space-2);
  border: 1px solid var(--ink);
  border-radius: var(--radius-tag);
  background: var(--paper);
  font: var(--t-body-strong);
}

/* Deceased (docs/plans/phase2-mortuary-and-notifications.md §6): solid ink with the word, so
   it is unmissable without a colour. */
.solidTag {
  padding: 0 var(--space-2);
  border: 1px solid var(--ink);
  border-radius: var(--radius-tag);
  background: var(--ink);
  color: var(--on-brand);
  font: var(--t-body-strong);
}

/* Phones: the banner stays sticky, so it is compact — tighter padding and gaps, the name
   one step smaller. Nothing is hidden; a long allergy list still wraps in full. */
@media (width < 640px) {
  .banner {
    min-height: 0;
    padding: var(--space-2) var(--gutter);
  }

  .line {
    gap: 0 var(--space-3);
  }

  .name {
    flex-basis: 100%;
    font: var(--t-heading);
    font-weight: 600;
  }

  .meta,
  .line {
    font: var(--t-small);
  }

  .strong,
  .allergyTag,
  .outlinedTag,
  .solidTag {
    font: var(--t-small);
    font-weight: 600;
  }
}

.visuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* Precautions in place (docs/plans/phase3-operations-modules.md §2.1): passive tags. */
.precautions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  margin: 0;
  padding: 0;
  list-style: none;
}
